import React, { useState, useEffect, useMemo } from 'react'
import SEO from '../components/SEO'
import { FaSearch, FaTimes, FaHome } from 'react-icons/fa'
import { motion } from 'framer-motion'
import PropertyCard from '../components/properties/PropertyCard'
import { getSocietiesFromSheet } from '../services/localDataService'

const Properties = () => {
  const [societies, setSocieties] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filters, setFilters] = useState({ search: '', location: 'all', flatType: 'all' })

  useEffect(() => {
    const fetchSocieties = async () => {
      try {
        const result = await getSocietiesFromSheet()
        if (result.success) {
          setSocieties(result.data)
        } else {
          console.error('Failed to fetch societies:', result.error)
          setError('Failed to load properties. Please try again later.')
        }
      } catch (err) {
        console.error('Error fetching societies:', err)
        setError('An error occurred while loading properties.')
      } finally {
        setLoading(false)
      }
    }
    fetchSocieties()
  }, [])

  // Derive unique filter options from grouped society data
  const locationOptions = useMemo(() =>
    [...new Set(societies.map(s => s.location).filter(Boolean))].sort(),
    [societies]
  )
  const flatTypeOptions = useMemo(() =>
    [...new Set(societies.flatMap(s => s.displayFlatTypes))].sort(),
    [societies]
  )

  // Apply filters
  const filtered = useMemo(() => {
    return societies.filter(s => {
      if (filters.search) {
        const q = filters.search.toLowerCase()
        if (
          !s.societyName.toLowerCase().includes(q) &&
          !s.location.toLowerCase().includes(q)
        ) return false
      }
      if (filters.location !== 'all' && s.location !== filters.location) return false
      if (filters.flatType !== 'all' && !s.displayFlatTypes.includes(filters.flatType)) return false
      return true
    })
  }, [societies, filters])

  const handleChange = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }
  const resetFilters = () => setFilters({ search: '', location: 'all', flatType: 'all' })
  const hasActive = filters.search || filters.location !== 'all' || filters.flatType !== 'all'

  return (
    <>
      <SEO 
        title="Properties - Find Your Perfect PG Accommodation | Chapter24"
        description="Browse through our premium PG accommodations for women. Filter by location and flat type to find your perfect home."
        url="/properties"
      />

      <section className="py-12 bg-gray-50 min-h-screen">
        <div className="container-custom">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">All Properties</h1>
            <p className="text-gray-500 text-sm">
              {loading ? 'Loading…' : `${filtered.length} societ${filtered.length !== 1 ? 'ies' : 'y'} found`}
            </p>
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">{error}</p>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-4 sm:p-6 mb-8">
            <div className="flex flex-wrap gap-3 items-center">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleChange}
                  placeholder="Search by society or location…"
                  className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                />
              </div>

              {/* Location */}
              <div className="min-w-[160px]">
                <select
                  name="location"
                  value={filters.location}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition bg-white cursor-pointer"
                >
                  <option value="all">All Locations</option>
                  {locationOptions.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>

              {/* Flat Type — pill tabs */}
              <div className="flex items-center gap-2">
                {['all', ...flatTypeOptions].map(ft => (
                  <button
                    key={ft}
                    onClick={() => setFilters(prev => ({ ...prev, flatType: ft }))}
                    className={`px-4 py-2.5 rounded-xl text-sm font-semibold border-2 transition ${
                      filters.flatType === ft
                        ? 'bg-primary-600 border-primary-600 text-white shadow-sm'
                        : 'border-gray-200 text-gray-600 hover:border-primary-400 hover:text-primary-600 bg-white'
                    }`}
                  >
                    {ft === 'all' ? 'All' : ft}
                  </button>
                ))}
              </div>

              {/* Clear */}
              {hasActive && (
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium transition"
                >
                  <FaTimes className="text-xs" /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-2xl shadow-md h-96 animate-pulse">
                  <div className="h-52 bg-gray-200 rounded-t-2xl" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                    <div className="h-12 bg-gray-200 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <FaHome className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Properties Found</h3>
              <p className="text-gray-400">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((society, index) => (
                <motion.div
                  key={society.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: index * 0.05 }}
                >
                  <PropertyCard property={society} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  )
}

export default Properties